import React, { useEffect, useState } from 'react';
import { Coins, Lock, Unlock, User, Phone, Mail, MapPin, MessageSquare, CheckCircle2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';

/**
 * ANIMATED EXAMPLES — looping mock-ups of the four moments that generate the
 * most support questions. Drawn from real UI patterns (balance card, Unlock
 * button, contact card, Call/Text/Email bar). Sample data only — never a real
 * homeowner.
 */

const useLoop = (phases: number, ms: number) => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setP((x) => (x + 1) % phases), ms);
    return () => window.clearInterval(id);
  }, [phases, ms]);
  return p;
};

const Frame: React.FC<{ n: number; title: string; caption: string; children: React.ReactNode }> = ({ n, title, caption, children }) => (
  <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      {children}
    </div>
    <p className="mt-4 text-xs font-black uppercase tracking-widest text-orange-500">{n}</p>
    <h3 className="mt-1 text-base font-extrabold text-slate-900">{title}</h3>
    <p className="mt-1 text-sm leading-relaxed text-slate-500">{caption}</p>
  </div>
);

const CreditsDemo: React.FC<{ es: boolean }> = ({ es }) => {
  const p = useLoop(3, 1600); // 0 → 0 credits, 1 → payment confirmed, 2 → 2 credits
  const n = p === 2 ? 2 : 0;
  return (
    <div className="w-full max-w-[220px] rounded-xl bg-white p-4 text-left shadow-xl">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{es ? 'Saldo de leads' : 'Lead balance'}</p>
      <div className="mt-1 flex items-center gap-2">
        <Coins className={`h-6 w-6 transition-colors ${n ? 'text-orange-500' : 'text-slate-300'}`} />
        <span key={n} className={`text-3xl font-black ${n ? 'text-slate-900 animate-scale-in' : 'text-slate-300'}`}>{n}</span>
        <span className="text-xs font-bold text-slate-400">{es ? 'créditos' : 'credits'}</span>
      </div>
      <div className={`mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold transition-all duration-500 ${p >= 1 ? 'bg-emerald-50 text-emerald-700 opacity-100' : 'opacity-0'}`}>
        <CheckCircle2 className="h-3.5 w-3.5" /> {es ? 'Pago confirmado' : 'Payment confirmed'}
      </div>
    </div>
  );
};

const UnlockDemo: React.FC<{ es: boolean }> = ({ es }) => {
  const p = useLoop(3, 1500); // 0 locked, 1 pressing, 2 unlocked
  return (
    <div className="w-full max-w-[230px] rounded-xl bg-white p-4 shadow-xl">
      <p className="text-sm font-extrabold text-slate-900">{es ? 'Pintura exterior' : 'Exterior painting'}</p>
      <p className="text-[11px] font-semibold text-slate-400">Holiday, FL · $3k–$6k</p>
      <button
        type="button"
        tabIndex={-1}
        className={`mt-3 flex w-full items-center justify-center gap-2 rounded-lg py-2 text-xs font-extrabold text-white transition-all duration-300 ${
          p === 2 ? 'bg-emerald-600' : 'bg-orange-500'
        } ${p === 1 ? 'scale-95 ring-4 ring-orange-300' : ''}`}
      >
        {p === 2 ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        {p === 2 ? (es ? 'Desbloqueado · −1 crédito' : 'Unlocked · −1 credit') : es ? 'Desbloquear lead' : 'Unlock Lead'}
      </button>
      <p className="mt-2 text-center text-[10px] font-bold text-slate-400">{es ? '1 de 3 contratistas' : '1 of 3 contractor slots'}</p>
    </div>
  );
};

const InfoDemo: React.FC<{ es: boolean }> = ({ es }) => {
  const p = useLoop(2, 2200);
  const rows = [
    [User, 'Maria G.'],
    [Phone, '(727) 555-0142'],
    [Mail, 'maria.g@example.com'],
    [MapPin, '1234 Palm Ave, Holiday, FL'],
  ] as const;
  return (
    <div className="w-full max-w-[240px] rounded-xl bg-white p-4 shadow-xl">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">{es ? 'Datos del cliente' : 'Homeowner details'}</p>
      {rows.map(([Icon, v], idx) => (
        <div key={v} className="flex items-center gap-2 py-0.5 text-xs font-semibold text-slate-800">
          <Icon className="h-3.5 w-3.5 shrink-0 text-orange-500" />
          <span
            className={`transition-all duration-500 ${p ? 'blur-0' : 'select-none blur-[4px]'}`}
            style={{ transitionDelay: p ? `${idx * 150}ms` : '0ms' }}
          >
            {v}
          </span>
        </div>
      ))}
    </div>
  );
};

const ContactDemo: React.FC<{ es: boolean }> = ({ es }) => {
  const p = useLoop(4, 1300); // highlight Call → Text → Email → stage "Contacted"
  const btns = [
    [Phone, es ? 'Llamar' : 'Call'],
    [MessageSquare, es ? 'Texto' : 'Text'],
    [Mail, 'Email'],
  ] as const;
  return (
    <div className="w-full max-w-[240px] rounded-xl bg-white p-4 shadow-xl">
      <div className="grid grid-cols-3 gap-2">
        {btns.map(([Icon, l], idx) => (
          <span
            key={l}
            className={`flex flex-col items-center gap-1 rounded-lg py-2 text-[11px] font-bold transition-all duration-300 ${
              p === idx ? 'scale-105 bg-orange-500 text-white shadow-lg' : 'bg-slate-100 text-slate-600'
            }`}
          >
            <Icon className="h-4 w-4" /> {l}
          </span>
        ))}
      </div>
      <div className={`mt-3 flex items-center justify-between rounded-lg border px-2 py-1.5 text-[11px] font-bold transition-colors ${p === 3 ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-400'}`}>
        <span>{es ? 'Etapa' : 'Stage'}</span>
        <span>{p === 3 ? (es ? 'Contactado' : 'Contacted') : es ? 'Comprado' : 'Purchased'}</span>
      </div>
    </div>
  );
};

const AnimatedExamples: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { lang } = useI18n();
  const es = lang === 'es';
  return (
    <div className={className}>
      <div className="mb-6 text-center">
        <p className="text-xs font-black uppercase tracking-widest text-orange-500">{es ? 'Véalo en acción' : 'See it in action'}</p>
        <h2 className="mt-2 text-2xl font-extrabold text-slate-900 sm:text-3xl">
          {es ? 'Lo que verá después de comprar' : 'What you will see after you buy'}
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-500">
          {es ? 'Ejemplos animados con datos de muestra.' : 'Animated examples using sample data.'}
        </p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Frame n={1} title={es ? 'Los créditos aparecen' : 'Credits appear'} caption={es ? 'Al confirmarse el pago, su saldo sube en el Panel y en cada proyecto.' : 'Once payment is confirmed, your balance updates on the Dashboard and every project page.'}>
          <CreditsDemo es={es} />
        </Frame>
        <Frame n={2} title={es ? 'Desbloquee un lead' : 'Unlock a lead'} caption={es ? 'Toque Desbloquear Lead en el proyecto. Se usa 1 crédito.' : 'Tap Unlock Lead on the project. One credit is used.'}>
          <UnlockDemo es={es} />
        </Frame>
        <Frame n={3} title={es ? 'Vea los datos del cliente' : 'View homeowner info'} caption={es ? 'Nombre, teléfono, correo y dirección se revelan al instante. También en Panel → Mis Leads.' : 'Name, phone, email and address are revealed instantly. Also in Dashboard → My Leads.'}>
          <InfoDemo es={es} />
        </Frame>
        <Frame n={4} title={es ? 'Contacte al cliente' : 'Contact the homeowner'} caption={es ? 'Llame, escriba o envíe correo desde el lead y marque la etapa.' : 'Call, text or email straight from the lead, then update its stage.'}>
          <ContactDemo es={es} />
        </Frame>
      </div>
    </div>
  );
};

export default AnimatedExamples;
